import {FC, useEffect, useState} from 'react'
import {Link, useLocation} from 'react-router-dom'
import {KTSVGSprite, checkIsActive} from '../../../_metronic/helpers'
import {MenuItem} from '../../types/MenuItem'
import {getMenuIcon, HOME_ICON} from './icons'
import {SIDEBAR_ICON_SPRITE_PATH} from './iconSprite.generated'

type Props = {
  items: MenuItem[]
  showHome?: boolean
}

// Um item contém a rota ativa em qualquer profundidade?
const containsActive = (item: MenuItem, pathname: string): boolean => {
  if (item.to && checkIsActive(pathname, item.to)) return true
  return (item.children || []).some((child) => containsActive(child, pathname))
}

const SidebarSpriteIcon: FC<{symbolId: string}> = ({symbolId}) => {
  const href = `${SIDEBAR_ICON_SPRITE_PATH}#${symbolId}`

  return (
    <span className='sb-item-icon'>
      <svg focusable='false' aria-hidden='true'>
        <use href={href} xlinkHref={href} />
      </svg>
    </span>
  )
}

const MenuIcon: FC<{item: MenuItem; section?: boolean}> = ({item, section = false}) => (
  <span className={`sb-icon-shell ${section ? 'sb-icon-shell-section' : ''}`} aria-hidden='true'>
    <SidebarSpriteIcon symbolId={getMenuIcon(item)} />
  </span>
)

const Leaf: FC<{item: MenuItem; depth: number}> = ({item, depth}) => {
  const {pathname} = useLocation()
  const isActive = item.to ? checkIsActive(pathname, item.to) : false

  return (
    <Link
      to={item.to || '#'}
      className={`sb-link ${isActive ? 'active' : ''} ${depth > 0 ? 'sb-link-sub' : ''}`}
      title={item.label}
    >
      <MenuIcon item={item} />
      <span className='sb-label'>{item.label}</span>
    </Link>
  )
}

const Accordion: FC<{item: MenuItem; depth: number}> = ({item, depth}) => {
  const {pathname} = useLocation()
  const hasActive = containsActive(item, pathname)
  const [open, setOpen] = useState(hasActive)

  // abrir automaticamente ao navegar para uma rota interna
  useEffect(() => {
    if (hasActive) setOpen(true)
  }, [hasActive])

  return (
    <div className={`sb-accordion ${open ? 'open' : ''}`}>
      <button
        type='button'
        className={`sb-link sb-accordion-toggle ${hasActive ? 'here' : ''} ${
          depth > 0 ? 'sb-link-sub' : ''
        }`}
        onClick={() => setOpen((value) => !value)}
        title={item.label}
        aria-expanded={open}
      >
        <MenuIcon item={item} />
        <span className='sb-label'>{item.label}</span>
        <KTSVGSprite icon='arr072.svg' className='svg-icon-5 sb-chevron' />
      </button>
      <div className='sb-accordion-body'>
        <div className='sb-accordion-inner'>
          {(item.children || []).map((child) => (
            <Node key={`${child.label}_${child.to}`} item={child} depth={depth + 1} />
          ))}
        </div>
      </div>
    </div>
  )
}

const Node: FC<{item: MenuItem; depth: number}> = ({item, depth}) => {
  if (item.children && item.children.length) return <Accordion item={item} depth={depth} />
  return <Leaf item={item} depth={depth} />
}

const SidebarMenu: FC<Props> = ({items, showHome = true}) => {
  const {pathname} = useLocation()
  const homeActive = checkIsActive(pathname, '/home')

  return (
    <nav className='sb-nav' aria-label='Navegação principal'>
      {showHome && (
        <Link
          to='/home'
          className={`sb-link sb-home ${homeActive ? 'active' : ''}`}
          title='Página Inicial'
        >
          <span className='sb-icon-shell' aria-hidden='true'>
            <SidebarSpriteIcon symbolId={HOME_ICON} />
          </span>
          <span className='sb-label'>Página Inicial</span>
        </Link>
      )}

      {items.map((section) =>
        section.isSection ? (
          <div className='sb-section' key={`${section.label}_section`}>
            <div className='sb-section-header' title={section.label}>
              <MenuIcon item={section} section />
              <span className='sb-section-title'>{section.label}</span>
            </div>
            <div className='sb-section-body'>
              {(section.children || []).map((child) => (
                <Node key={`${child.label}_${child.to}`} item={child} depth={0} />
              ))}
            </div>
          </div>
        ) : (
          <Node key={`${section.label}_${section.to}`} item={section} depth={0} />
        )
      )}
    </nav>
  )
}

export {SidebarMenu}
